import { supabase } from './supabase'
import type { CaptainRole } from '../types/captain'

// ---------------------------------------------------------------------------
// Types — mirrors of the SECURITY DEFINER RPC return shapes in the backend.
// Keep these aligned with supabase/migrations/20260513120000_* and 20260509120000_*.
// ---------------------------------------------------------------------------

export interface PortalGroup {
  id: string
  slug: string
  name: string
  description: string
  privacy: 'public' | 'unlisted'
  profile_image_path: string | null
  hero_image_path: string | null
  member_count: number
  created_at: string
}

export type PortalCaptainRole = 'organizer' | 'captain'

export interface RoleSlotApi {
  role: CaptainRole
  /**
   * ON-334: minimum target headcount for this role, not a hard cap — the
   * backend accepts sign-ups beyond this number (unlimited overflow). Field
   * name kept as `slots` to match the `list_group_events_needing_captains`
   * RPC's JSON key; see RoleSlot.target for the UI-facing name.
   */
  slots: number
  filled: number
}

export interface EventNeedingCaptains {
  event_id: string
  title: string
  event_datetime: string
  start_location_name: string | null
  roles: RoleSlotApi[]
}

export type SignupBucket = 'upcoming' | 'past'

export interface MyCaptainSignup {
  bucket: SignupBucket
  signup_id: string
  event_id: string
  group_id: string
  event_title: string
  event_datetime: string
  start_location_name: string | null
  role: CaptainRole
  status: 'confirmed' | 'cancelled'
}

export interface EventRosterEntry {
  signup_id: string
  role: CaptainRole
  display_name: string | null
  avatar_url: string | null
  created_at: string
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/** Anon-callable. Returns null when the slug doesn't resolve. */
export async function getGroupBySlug(slug: string): Promise<PortalGroup | null> {
  const { data, error } = await supabase.rpc('get_group_by_slug', { p_slug: slug })
  if (error) throw error
  return (data as PortalGroup | null) ?? null
}

/** Authenticated only. Throws on `unauthenticated` / `group_not_found`. */
export async function getMyClubCaptainRole(groupId: string): Promise<PortalCaptainRole> {
  const { data, error } = await supabase.rpc('get_my_club_captain_role', {
    p_group_id: groupId,
  })
  if (error) throw error
  return data as PortalCaptainRole
}

export async function listGroupEventsNeedingCaptains(
  groupId: string
): Promise<EventNeedingCaptains[]> {
  const { data, error } = await supabase.rpc('list_group_events_needing_captains', {
    p_group_id: groupId,
  })
  if (error) throw error
  return (data ?? []) as EventNeedingCaptains[]
}

export async function listMyCaptainSignups(): Promise<MyCaptainSignup[]> {
  const { data, error } = await supabase.rpc('list_my_captain_signups')
  if (error) throw error
  return (data ?? []) as MyCaptainSignup[]
}

export async function listEventCaptainRoster(eventId: string): Promise<EventRosterEntry[]> {
  const { data, error } = await supabase.rpc('list_event_captain_roster', {
    p_event_id: eventId,
  })
  if (error) throw error
  return (data ?? []) as EventRosterEntry[]
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

export async function signupForCaptainRole(
  eventId: string,
  role: CaptainRole
): Promise<string> {
  const { data, error } = await supabase.rpc('signup_for_captain_role', {
    p_event_id: eventId,
    p_role: role,
  })
  if (error) throw error
  return data as string
}

export async function cancelCaptainSignup(signupId: string): Promise<string> {
  const { data, error } = await supabase.rpc('cancel_captain_signup', {
    p_signup_id: signupId,
  })
  if (error) throw error
  return data as string
}

export async function changeCaptainRole(
  signupId: string,
  newRole: CaptainRole
): Promise<string> {
  const { data, error } = await supabase.rpc('change_captain_role', {
    p_signup_id: signupId,
    p_new_role: newRole,
  })
  if (error) throw error
  return data as string
}

// ---------------------------------------------------------------------------
// Error mapping — translates the RPC error messages into user-facing copy.
// The backend raises with stable text codes (e.g. "role_not_offered") which
// we map to UI strings here rather than scattering the mapping across
// components.
//
// ON-334: "slot_full" is no longer raised by signup_for_captain_role /
// change_captain_role — role headcounts are a minimum target with unlimited
// overflow, so sign-ups are never rejected for a role being "full". Removed
// from this map accordingly (see running-club-backend migration
// 20260711100000_on334_captain_role_target_overflow.sql).
// ---------------------------------------------------------------------------

const SIGNUP_ERROR_MESSAGES: Record<string, string> = {
  event_already_started: 'This event has already started. Sign-ups are closed.',
  event_not_flagged: 'This event is no longer accepting captain sign-ups.',
  event_not_found: "We couldn't find this event. It may have been removed.",
  signup_not_found: "We couldn't find your sign-up. Please refresh and try again.",
  not_confirmed: 'Your sign-up is no longer active.',
  already_signed_up: 'You are already signed up for this event.',
  role_not_offered: 'This role is not being offered for this event.',
  user_blocked: 'You are not eligible to volunteer for this club.',
  not_eligible:
    "You're not eligible to sign up as a captain for this club yet. Ask your club organizer to add you as an admin.",
  unauthenticated: 'Please sign in again to continue.',
  forbidden: "You don't have permission to do that.",
}

export function describeCaptainRpcError(error: unknown): string {
  const message = (error as { message?: string })?.message ?? ''
  for (const code of Object.keys(SIGNUP_ERROR_MESSAGES)) {
    if (message.includes(code)) return SIGNUP_ERROR_MESSAGES[code]
  }
  return 'Something went wrong. Please try again.'
}
